import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, Globe, LogOut } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import AskVytali from "./AskVytali";

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  if (!user) return null;

  const links = [
    { to: "/", label: t("nav.dashboard") },
    { to: "/courses", label: t("nav.courses") },
    { to: "/journey", label: t("nav.journey") },
  ];
  if (["TRAINER", "FORMATEUR", "CORPORATE_TRAINER", "ACADEMY_ADMIN", "ADMIN", "SUPER_ADMIN", "OWNER"].includes(user.system_role))
    links.push({ to: "/trainer", label: t("nav.trainer") });
  if (["ACADEMY_ADMIN", "ADMIN", "SUPER_ADMIN", "OWNER"].includes(user.system_role))
    links.push({ to: "/admin", label: t("nav.admin") });

  const toggleLang = () => {
    const next = i18n.language === "fr" ? "en" : "fr";
    i18n.changeLanguage(next);
    localStorage.setItem("vytali_lang", next);
  };

  return (
    <div className="min-h-screen">
      <header className="glass-header sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="nav-brand" className="flex items-center gap-2.5 font-heading font-semibold text-lg tracking-tight">
            <img src="/brand/emblem.svg" alt="Vytali Academy" className="h-10 w-auto" />
            <span className="hidden sm:inline text-[#0A1F44]">{t("brand")}</span>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`nav-link-${l.to.replace("/", "") || "dashboard"}`}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${location.pathname === l.to ? "bg-[#002BF6] text-white" : "text-foreground hover:bg-muted"}`}>
                {l.label}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <button onClick={toggleLang} data-testid="lang-toggle"
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-mono-v font-semibold border border-border rounded-md hover:bg-muted transition-colors duration-200 uppercase">
              <Globe size={13} /> {i18n.language === "fr" ? "FR" : "EN"}
            </button>
            <div className="hidden sm:flex flex-col items-end mr-1">
              <span className="text-xs font-medium leading-tight">{user.name}</span>
              <span className="text-[10px] text-muted-foreground font-mono-v">{user.system_role}</span>
            </div>
            <button data-testid="logout-btn" onClick={async () => { await logout(); navigate("/login"); }}
              className="p-2 rounded-md hover:bg-muted transition-colors duration-200" title={t("nav.logout")}>
              <LogOut size={16} />
            </button>
            <button className="md:hidden p-2" data-testid="mobile-menu-btn" onClick={() => setOpen(!open)}>
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
        {open && (
          <nav className="md:hidden border-t border-border bg-white px-4 py-3 flex flex-col gap-1">
            {links.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${l.to.replace("/", "") || "dashboard"}`}
                className={`px-3 py-2.5 text-sm font-medium rounded-md ${location.pathname === l.to ? "bg-[#002BF6] text-white" : "hover:bg-muted"}`}>
                {l.label}
              </Link>
            ))}
          </nav>
        )}
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 pt-8 pb-28 sm:pb-24">{children}</main>
      <AskVytali page={location.pathname} />
    </div>
  );
};

export default Layout;
